import { Injectable, signal } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { getAuthHeadersFromUserData } from '../../shared/utils/auth-headers.util';
import { Observable, map, catchError, throwError } from 'rxjs';
import {
  FleetClient,
  FleetStats,
  FleetApiResponse,
  FleetApiData,
  FleetListRequest,
  FleetPagination,
  FleetInvoiceRecord,
  FleetPlateInput,
} from '../models/fleet.model';
import { environment } from '../../../environments/environment';
import { getUserTimezone } from '../../shared/utils/date.util';

/**
 * Singleton Angular service that manages fleet-related API interactions and local reactive state (fleet clients, loading, error, pagination).
 * @component
 * @selector app-fleet-service
 * @example
 *   // Inject via constructor in a component or another service:
 *   constructor(private fleetService: FleetService) {}
 * @input None - This service has no Angular @Input properties; it exposes reactive getters and Observables instead.
 * @output None - This service does not emit Angular @Output events; it exposes Observables and internal signals for consumers to subscribe to.
 *
 * Responsibilities:
 * - Fetch and manage fleet lists (loadFleets) with filtering, pagination and error handling.
 * - Perform CRUD operations for fleets (createFleet, updateFleet) and keep local state in sync.
 * - Retrieve invoice, service history, and audit log (getInvoiceData, getServiceHistory, getAuditLog).
 * - Manage license plates: getLicensePlates, importLicensePlates, updateLicensePlate, deleteLicensePlate(s).
 * - Map raw API responses to frontend FleetClient objects via mapApiDataToFleetClients.
 * - Provide aggregated statistics and UI-friendly card definitions via getFleetStatistics, getFleetStats, getFleetStatsWithIcons.
 * - Maintain reactive local state using signals: fleetClients, loading, error, pagination with corresponding getters.
 *
 * Key methods:
 * - loadFleets(request: FleetListRequest): Observable<{ fleets: FleetClient[]; pagination: any }>
 *     Builds query parameters from the request, calls the fleet list API, updates fleetClients and pagination signals, and handles loading/error state.
 * - getInvoiceData(fleetId: string, startDate: string, endDate: string): Observable<any>
 *     Posts date range to retrieve invoice data for a specific fleet.
 * - getServiceHistory(fleetId: string, options?): Observable<any>
 * - getAuditLog(fleetId: string, options?): Observable<any>
 * - getEmailHistory(fleetId: string, options?): Observable<any>
 *     Fetches the service/invoice history for a fleet (includes client timezone query param for server-side date handling).
 * - createFleet(fleetData: any): Observable<any>
 *     Creates a new fleet and reloads the fleet list on success.
 * - updateFleet(fleetId: string, fleetData: any): Observable<any>
 *     Updates an existing fleet and reloads the fleet list on success.
 * - getFleetStatistics(): Observable<any>
 *     Retrieves aggregated statistics from the API (preferred for accurate totals).
 * - getFleetStats(): FleetStats
 *     Computes fallback statistics from local (current page) data when API totals are not available.
 * - getFleetStatsWithIcons(apiStats?: any): any[]
 *     Produces an array of UI card definitions (title, value, color, icon, styles) using API stats or fallback values.
 * - addFleetClient(client: Omit<FleetClient, 'id'>): void / updateFleetClient(id: string, updates: Partial<FleetClient>): void / deleteFleetClient(id: string): void
 *     Local mutations to the fleetClients signal for optimistic updates.
 * - License plate helpers: getLicensePlates, importLicensePlates, updateLicensePlate, deleteLicensePlate(s)
 *     CRUD operations against license-plate-related endpoints.
 *
 * Important implementation details:
 * - Provided in root (singleton) via @Injectable({ providedIn: 'root' }).
 * - Uses HttpClient for API calls and a private getHeaders() helper to include authentication and JSON content type.
 * - Base URL includes `environment.apiVersion` (e.g. `/api/v1/...`) like ApiService.
 * - Uses RxJS operators (map, catchError) to transform responses and handle errors; errors update the service error signal and reset loading state.
 * - mapApiDataToFleetClients normalizes API payloads into FleetClient structures (e.g., human-readable duration strings, license plate placeholders).
 * - Pagination.total returned by the API is used for overall counts; some aggregates (totalVehicles, monthlyRevenue) are calculated from the current page and may be approximate.
 * - No Angular component lifecycle hooks are used (services do not implement component lifecycle hooks); consumers should manage subscriptions to returned Observables.
 */
@Injectable({
  providedIn: 'root',
})
export class FleetService {
  /** Same versioned root as `ApiService` — `environment.apiVersion` (e.g. v1). */
  private readonly baseUrl = `${environment.baseUrl}/${environment.apiVersion}`;

  // Signals for reactive state management
  private readonly fleetClients = signal<FleetClient[]>([]);
  private readonly loading = signal<boolean>(false);
  private readonly error = signal<string | null>(null);
  private readonly pagination = signal<{ page: number; limit: number; total: number }>({
    page: 1,
    limit: 10,
    total: 0,
  });

  constructor(private http: HttpClient) {}

  // Getters for reactive state
  getFleetClients() {
    return this.fleetClients();
  }

  getLoading() {
    return this.loading();
  }

  getError() {
    return this.error();
  }

  getPagination() {
    return this.pagination();
  }

  private getHeaders() {
    return getAuthHeadersFromUserData({ includeJsonContentType: true });
  }

  private appendFleetListQueryParams(params: URLSearchParams, request: FleetListRequest): void {
    if (request.page) params.set('page', request.page.toString());
    if (request.limit) params.set('limit', request.limit.toString());
    if (request.billing_cycle_type) {
      const cycles = request.billing_cycle_type
        .split(',')
        .map(c => c.trim().toLowerCase())
        .join(',');
      params.set('billing_cycle_type', cycles);
    }
    if (request.status) {
      const statuses = request.status
        .split(',')
        .map(s => s.trim().toLowerCase())
        .join(',');
      params.set('status', statuses);
    }
    if (request.search) params.set('search', request.search);
    if (request.sort_by) params.set('sort_by', request.sort_by);
    if (request.order_by) params.set('order_by', request.order_by);
    if (request.location_id != null && String(request.location_id).trim() !== '') {
      params.set('location_id', String(request.location_id).trim());
    }
    if (request.contract_duration_months) {
      const durations = request.contract_duration_months
        .split(',')
        .map(d => d.trim())
        .filter(Boolean)
        .join(',');
      if (durations) params.set('contract_duration_months', durations);
    }
  }

  private fleetListUrl(request: FleetListRequest): string {
    const params = new URLSearchParams();
    this.appendFleetListQueryParams(params, request);
    // Trailing `?` when empty matches existing callers/tests (`.../fleet?`).
    return `${this.baseUrl}/fleet?${params.toString()}`;
  }

  /**
   * Same `GET /fleet` as {@link loadFleets} without mutating in-memory list / loading signals
   * (e.g. order-request / notification flows elsewhere in the app).
   */
  queryFleets(
    request: FleetListRequest = {}
  ): Observable<{ fleets: FleetClient[]; pagination: FleetPagination }> {
    const url = this.fleetListUrl(request);
    return this.http.get<FleetApiResponse>(url, { headers: this.getHeaders() }).pipe(
      map((response: FleetApiResponse) => {
        if (response.status && response.data) {
          const data = response.data as any;
          let fleetData: FleetApiData[] = [];

          if (Array.isArray(data)) {
            fleetData = data;
          } else if (data.fleets && Array.isArray(data.fleets)) {
            fleetData = data.fleets;
          } else if (data.Fleets && Array.isArray(data.Fleets)) {
            fleetData = data.Fleets;
          }

          return {
            fleets: this.mapApiDataToFleetClients(fleetData),
            pagination: data.pagination || {
              page: 1,
              limit: fleetData.length || 10,
              total: fleetData.length || 0,
            },
          };
        }
        throw new Error(response.message || 'Failed to load fleets');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Load fleet data from API
   */
  loadFleets(
    request: FleetListRequest = {}
  ): Observable<{ fleets: FleetClient[]; pagination: any }> {
    this.loading.set(true);
    this.error.set(null);

    const url = this.fleetListUrl(request);

    return this.http.get<FleetApiResponse>(url, { headers: this.getHeaders() }).pipe(
      map((response: FleetApiResponse) => {
        if (response.status && response.data) {
          const data = response.data as any;
          let fleetData: FleetApiData[] = [];

          if (Array.isArray(data)) {
            fleetData = data;
          } else if (data.fleets && Array.isArray(data.fleets)) {
            fleetData = data.fleets;
          } else if (data.Fleets && Array.isArray(data.Fleets)) {
            fleetData = data.Fleets;
          }

          const fleets = this.mapApiDataToFleetClients(fleetData);
          const pagination = data.pagination || {
            page: 1,
            limit: fleetData.length || 10,
            total: fleetData.length || 0,
          };

          this.fleetClients.set(fleets);
          this.pagination.set(pagination);
          this.loading.set(false);
          return { fleets, pagination };
        }
        throw new Error(response.message || 'Failed to load fleets');
      }),
      catchError(error => {
        this.error.set(error.message || 'An error occurred while loading fleets');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Preview invoice period totals (does not persist an invoice).
   */
  getInvoiceData(fleetId: string, startDate: string, endDate: string): Observable<any> {
    const body = {
      start_date: startDate,
      end_date: endDate,
    };

    return this.http
      .post(`${this.baseUrl}/fleet/show-invoice-data/${fleetId}`, body, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error fetching invoice data:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * List persisted invoices for a fleet (past invoice accordion).
   */
  getInvoices(fleetId: string): Observable<FleetInvoiceRecord[]> {
    const params = new HttpParams().set('timezone', getUserTimezone());
    return this.http
      .get<{ status: boolean; data: FleetInvoiceRecord[] }>(
        `${this.baseUrl}/fleet/invoice/${fleetId}`,
        { headers: this.getHeaders(), params }
      )
      .pipe(
        map(response => {
          if (response.status && Array.isArray(response.data)) {
            return response.data;
          }
          throw new Error('Failed to load invoices');
        }),
        catchError(error => {
          console.error('Error fetching invoices:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Create (or reuse) a staff invoice for a period; returns PDF soft-fail fields.
   */
  createInvoice(
    fleetId: string,
    body: { period_start: string; period_end: string; issue_date?: string; timezone?: string }
  ): Observable<FleetInvoiceRecord> {
    const payload = {
      ...body,
      timezone: body.timezone || getUserTimezone(),
    };
    return this.http
      .post<{ status: boolean; data: FleetInvoiceRecord; message?: string }>(
        `${this.baseUrl}/fleet/invoice/${fleetId}`,
        payload,
        { headers: this.getHeaders() }
      )
      .pipe(
        map(response => {
          if (response.status && response.data) {
            return response.data;
          }
          throw new Error(response.message || 'Failed to create invoice');
        }),
        catchError(error => {
          console.error('Error creating invoice:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Ensure a signed PDF URL exists for an invoice.
   */
  getInvoicePdf(fleetId: string, invoiceId: string): Observable<FleetInvoiceRecord> {
    const params = new HttpParams().set('timezone', getUserTimezone());
    return this.http
      .get<{ status: boolean; data: FleetInvoiceRecord; message?: string }>(
        `${this.baseUrl}/fleet/invoice/${fleetId}/pdf/${invoiceId}`,
        { headers: this.getHeaders(), params }
      )
      .pipe(
        map(response => {
          if (response.status && response.data) {
            return response.data;
          }
          throw new Error(response.message || 'Failed to load invoice PDF');
        }),
        catchError(error => {
          console.error('Error fetching invoice PDF:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get per-visit service history for a fleet (paginated).
   */
  getServiceHistory(
    fleetId: string,
    options: { page?: number; limit?: number } = {}
  ): Observable<any> {
    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    const params = new HttpParams()
      .set('timezone', getUserTimezone())
      .set('page', String(page))
      .set('limit', String(limit));
    return this.http
      .get(`${this.baseUrl}/fleet/${fleetId}/service-history`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error fetching service history:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get fleet edit/audit history (paginated).
   */
  getAuditLog(fleetId: string, options: { page?: number; limit?: number } = {}): Observable<any> {
    const page = options.page ?? 1;
    const limit = options.limit ?? 100;
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    return this.http
      .get(`${this.baseUrl}/fleet/${fleetId}/audit-log`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error fetching fleet audit log:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Get fleet invoice email attempts (paginated), one row per recipient per send.
   */
  getEmailHistory(
    fleetId: string,
    options: { page?: number; limit?: number } = {}
  ): Observable<any> {
    const page = options.page ?? 1;
    const limit = options.limit ?? 10;
    const params = new HttpParams().set('page', String(page)).set('limit', String(limit));
    return this.http
      .get(`${this.baseUrl}/fleet/${fleetId}/email-history`, {
        headers: this.getHeaders(),
        params,
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error fetching fleet email history:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Map API data to frontend FleetClient interface
   */
  private formatBillingCycleType(value: string | undefined): string {
    switch ((value || 'monthly').toLowerCase()) {
      case 'weekly':
        return 'Weekly';
      case 'biweekly':
        return 'Bi-weekly';
      case 'custom':
        return 'Custom';
      case 'monthly':
      default:
        return 'Monthly';
    }
  }

  /** Formats an ISO/plain date string as "Mar 1, 2027" (used by the Billing Cycle column). */
  private formatCalendarDate(value: string | undefined): string {
    if (!value) return '';
    // Calendar YYYY-MM-DD must not use `new Date(ymd)` (UTC midnight → prior day west of UTC).
    const ymd = String(value).trim().split('T')[0];
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd);
    if (match) {
      const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
      return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC',
      }).format(date);
    }
    const date = new Date(value);
    if (isNaN(date.getTime())) return value;
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  /** Formats the Billing Cycle column as "{start date} {frequency}", e.g. "Oct 1, 2026 Monthly". */
  private formatBillingCycleDisplay(
    startDate: string | undefined,
    billingCycleType: string | undefined
  ): string {
    const datePart = this.formatCalendarDate(startDate);
    const cyclePart = this.formatBillingCycleType(billingCycleType);
    return datePart ? `${datePart} ${cyclePart}` : cyclePart;
  }

  private mapApiDataToFleetClients(apiData: FleetApiData[]): FleetClient[] {
    if (!Array.isArray(apiData)) {
      return [];
    }
    return apiData.map(item => {
      const months = item.contract_duration_months;
      const durationStr =
        months != null && months > 0 ? `${months} month${months > 1 ? 's' : ''}` : '0 months';

      return {
        id: item.id,
        clientName: item.business_name || '',
        pricePerVehicle: item.price_per_vehicle || 0,
        pricingType: item.pricing_type === 'per_wash' ? 'per_wash' : 'per_vehicle',
        package: item.package_name || '',
        duration: durationStr,
        billingCycleType: this.formatBillingCycleType(item.billing_cycle_type),
        billingCycleDisplay: this.formatBillingCycleDisplay(
          item.start_date,
          item.billing_cycle_type
        ),
        avgWashesPerMonth: item.avg_washes_month ?? null,
        vehicles: item.is_license_plate_added ? 'View license plates' : 'Add license plates',
        licensePlates: [],
        isLicensePlateAdded: !!item.is_license_plate_added,
        locationId: item.location_id || '',
        branch: item.branch || '',
        vehicleCount: item.vehicle_count_declared || 0,
        licensePlateCount: Number(item.license_plate_count) || 0,
        paymentTermDays: item.payment_term_days ?? 30,
        startDate: item.start_date || '',
        // Inactive is the stored flag and wins over the derived contract window.
        status: item.status === 'inactive' ? 'inactive' : item.contract_status || 'active',
        contactNumber: item.contact_number || '',
        email: item.email || '',
        notificationRecipients: item.notification_recipients || [],
        billingCycleTypeRaw: item.billing_cycle_type || 'monthly',
        billingCycleAnchor: item.billing_cycle_anchor || item.start_date || '',
      };
    });
  }

  /**
   * Fetch a single fleet by ID (full detail row for edit prefill).
   */
  getFleetById(fleetId: string): Observable<FleetApiData> {
    const url = `${this.baseUrl}/fleet/${fleetId}`;
    return this.http.get<FleetApiResponse>(url, { headers: this.getHeaders() }).pipe(
      map((response: any) => {
        if (response.status && response.data) {
          return response.data as FleetApiData;
        }
        throw new Error(response.message || 'Failed to load fleet');
      }),
      catchError(error => throwError(() => error))
    );
  }

  /**
   * Create a new fleet
   */
  createFleet(fleetData: any): Observable<any> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${this.baseUrl}/fleet`;

    return this.http.post<any>(url, fleetData, { headers: this.getHeaders() }).pipe(
      map((response: any) => {
        this.loading.set(false);
        return response;
      }),
      catchError(error => {
        this.error.set(error.message || 'An error occurred while creating fleet');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Update an existing fleet
   */
  updateFleet(fleetId: string, fleetData: any): Observable<any> {
    this.loading.set(true);
    this.error.set(null);

    const url = `${this.baseUrl}/fleet/${fleetId}`;

    return this.http.put<any>(url, fleetData, { headers: this.getHeaders() }).pipe(
      map((response: any) => {
        this.loading.set(false);
        return response;
      }),
      catchError(error => {
        this.error.set(error.message || 'An error occurred while updating fleet');
        this.loading.set(false);
        return throwError(() => error);
      })
    );
  }

  /**
   * Get fleet statistics from API
   */
  getFleetStatistics(): Observable<any> {
    const url = `${this.baseUrl}/fleet/statistics`;

    return this.http.get<any>(url, { headers: this.getHeaders() }).pipe(
      map((response: any) => {
        if (response.status && response.data) {
          return response.data;
        }
        throw new Error(response.message || 'Failed to load fleet statistics');
      }),
      catchError(error => {
        console.error('Error loading fleet statistics:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Fallback stats from list pagination / current page when GET /fleet/statistics fails.
   * Does not invent monthly revenue — that comes from the statistics API only.
   */
  getFleetStats(): FleetStats {
    const fleets = this.fleetClients();
    const pagination = this.pagination();
    const totalFleets = pagination.total;
    const totalVehicles = fleets.reduce((sum, fleet) => sum + (fleet.licensePlateCount || 0), 0);

    return {
      totalFleets,
      totalVehicles,
      activeContracts: totalFleets,
      monthlyRevenue: 0,
    };
  }

  /**
   * Returns an array of fleet statistic objects with display metadata and icon configuration, using API stats if provided or falling back to computed stats.
   * @example
   * etFleetStatsWithIcons({ license_plate_count: '10', fleet_count: '3', active_fleet_count: '2', total_amount: '12345.67' })
   * [{ title: 'Total Vehicles', value: '10', color: 'blue', icon: 'assets/images/fleet-icons/total-vehicles-icon.svg', ... }, ...]
   * @param {any} apiStats - Optional API statistics object (e.g. license_plate_count, fleet_count, active_fleet_count, total_amount).
   * @returns {Array<Object>} An array of statistic objects each containing title, value, color, icon, iconClass and iconStyle.
   **/
  getFleetStatsWithIcons(apiStats?: any) {
    const stats = this.getFleetStats();

    return [
      {
        title: 'Total Fleets',
        value: apiStats ? apiStats.fleet_count || '0' : stats.totalFleets,
        color: 'green',
        icon: 'assets/images/fleet-icons/total-fleets-icon.svg',
        iconClass: 'total-fleets-icon',
        iconStyle: {
          position: 'absolute',
          right: '0px',
          bottom: '-10px',
          width: '120px',
          height: '120px',
          transform: 'rotate(-4deg)',
          opacity: '0.9',
          'image-rendering': '-webkit-optimize-contrast',
          filter: 'none',
          'z-index': '3',
        },
      },
      {
        title: 'Total Vehicles',
        value: apiStats ? apiStats.license_plate_count || '0' : stats.totalVehicles,
        color: 'blue',
        icon: 'assets/images/fleet-icons/total-vehicles-icon.svg',
        iconClass: 'total-vehicles-icon',
        iconStyle: {
          position: 'absolute',
          right: '0px',
          bottom: '-10px',
          width: '120px',
          height: '120px',
          transform: 'rotate(-4deg)',
          'image-rendering': '-webkit-optimize-contrast',
          filter: 'none',
        },
      },
      {
        title: 'Active Contracts',
        value: apiStats ? apiStats.active_fleet_count || '0' : stats.activeContracts,
        color: 'purple',
        icon: 'assets/images/fleet-icons/active-contracts-icon.svg',
        iconClass: 'active-contracts-icon',
        iconStyle: {
          position: 'absolute',
          right: '0px',
          bottom: '-8px',
          width: '112px',
          height: '112px',
          transform: 'rotate(4deg)',
          opacity: '0.9',
          'image-rendering': '-webkit-optimize-contrast',
          filter: 'none',
          'z-index': '3',
        },
      },
      {
        title: 'Monthly Revenue',
        value: apiStats?.total_amount
          ? `$${parseFloat(apiStats.total_amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
          : '$0.00',
        color: 'orange',
        icon: 'assets/images/fleet-icons/monthly-revenue-icon.svg',
        iconClass: 'monthly-revenue-icon',
        iconStyle: {
          position: 'absolute',
          right: '-10px',
          bottom: '-10px',
          width: '120px',
          height: '120px',
          transform: 'rotate(-4deg)',
          'image-rendering': '-webkit-optimize-contrast',
          filter: 'none',
        },
      },
    ];
  }

  /**
   * Add new fleet client
   */
  addFleetClient(client: Omit<FleetClient, 'id'>): void {
    const newClient: FleetClient = {
      ...client,
      id: Date.now().toString(),
    };
    this.fleetClients.update(clients => [newClient, ...clients]);
  }

  /**
   * Update fleet client
   */
  updateFleetClient(id: string, updates: Partial<FleetClient>): void {
    this.fleetClients.update(clients =>
      clients.map(client => (client.id === id ? { ...client, ...updates } : client))
    );
  }

  /**
   * Delete fleet client
   */
  deleteFleetClient(id: string): void {
    this.fleetClients.update(clients => clients.filter(client => client.id !== id));
  }

  /**
   * Get license plates for a specific fleet
   */
  getLicensePlates(fleetId: string): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/fleet/${fleetId}/license-plates`, { headers: this.getHeaders() })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error fetching license plates:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Import license plates for a specific fleet. Bare strings carry no state; the server
   * fills it from the fleet's locations when they share one state.
   */
  importLicensePlates(
    fleetId: string,
    licensePlates: (string | FleetPlateInput)[]
  ): Observable<any> {
    const payload = {
      license_plates: licensePlates,
    };

    return this.http
      .post(`${this.baseUrl}/fleet/${fleetId}/license-plates`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error importing license plates:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Update a fleet license plate and its state (null clears the state).
   */
  updateLicensePlate(plateId: string, licensePlate: string, state: string | null): Observable<any> {
    const payload = {
      license_plate: licensePlate,
      state,
    };

    return this.http
      .put(`${this.baseUrl}/fleet/license-plate/${plateId}`, payload, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error updating license plate:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Delete a single license plate
   */
  deleteLicensePlate(plateId: string): Observable<any> {
    return this.http
      .delete(`${this.baseUrl}/fleet/license-plate/${plateId}`, {
        headers: this.getHeaders(),
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error deleting license plate:', error);
          return throwError(() => error);
        })
      );
  }

  /**
   * Delete multiple license plates
   */
  deleteLicensePlates(plateIds: string[]): Observable<any> {
    const payload = {
      plate_ids: plateIds,
    };

    return this.http
      .delete(`${this.baseUrl}/fleet/license-plates`, {
        headers: this.getHeaders(),
        body: JSON.stringify(payload),
      })
      .pipe(
        map((response: any) => response),
        catchError(error => {
          console.error('Error deleting license plates:', error);
          return throwError(() => error);
        })
      );
  }
}
